import ITManagement from "@/assets/images/breadcrumbs/it-service-content.webp";
import { Activity, Users, Settings, DollarSign } from "lucide-react";

export default function ITManagementSection() {
    const cards = [
        {
            title: "Proactive Monitoring",
            description: "We monitor your IT systems around the clock to identify & resolve issues before they impact operations.",
            icon: Activity,
        },
        {
            title: "Expert Technicians",
            description: "Our certified IT professionals bring years of experience ensuring top-notch support and solutions.",
            icon: Users,
        },
        {
            title: "Customized Solutions",
            description: "Tailored IT management services to meet your specific needs, whether startup or large enterprise.",
            icon: Settings,
        },
        {
            title: "Cost Efficiency",
            description: "Optimize your IT spending and ensure you get the best value from your technology investments.",
            icon: DollarSign,
        },
    ];

    return (
        <section className="relative py-12 md:pt-24 md:pb-16 overflow-hidden">
            <style>{`
                .flip-card { perspective: 1000px; }
                .flip-card-inner {
                    position: relative;
                    width: 100%;
                    height: 100%;
                    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
                    transform-style: preserve-3d;
                }
                .flip-card-front, .flip-card-back {
                    position: absolute;
                    width: 100%;
                    height: 100%;
                    backface-visibility: hidden;
                    -webkit-backface-visibility: hidden;
                    border-radius: 0.75rem;
                }
                .flip-card-back { transform: rotateY(180deg); }

                @media (min-width: 1024px) {
                    .flip-card:hover .flip-card-inner { transform: rotateY(180deg); }
                }
            `}</style>

            <div className="container">
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">

                    {/* ── Image ── */}
                    <div className="relative order-1 lg:order-none">
                        <div
                            className="overflow-hidden rounded-2xl h-[240px] sm:h-[320px] md:h-[400px] lg:h-[520px]"
                            style={{ boxShadow: "0 32px 64px rgba(0,0,0,0.6)" }}
                        >
                            <img
                                src={ITManagement}
                                alt="IT Management Services at SAN micron"
                                className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                            />
                        </div>

                        {/* Badge */}
                        <div
                            className="absolute -bottom-5 right-4 md:right-6 rounded-2xl px-4 sm:px-5 py-3 sm:py-4 text-center border"
                            style={{
                                backgroundColor: "#0d0d0d",
                                borderColor: "rgba(255,255,255,0.08)",
                                boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                            }}
                        >
                            <p className="text-2xl sm:text-3xl font-bold leading-none bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent">
                                24/7
                            </p>
                            <p className="text-xs sm:text-sm mt-1" style={{ color: "rgba(255,255,255,0.45)" }}>
                                System Monitoring
                            </p>
                        </div>
                    </div>

                    {/* ── Content ── */}
                    <div className="space-y-4 sm:space-y-6 order-2 lg:order-none mt-6 sm:mt-8 lg:mt-0">

                        {/* Headline */}
                        <h2 className="text-2xl sm:text-3xl md:text-[2.6rem] font-bold text-white leading-snug">
                            Our IT Management
                            <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400 bg-clip-text text-transparent pl-2">
                                Services
                            </span>
                        </h2>

                        {/* Divider */}
                        <div className="w-12 h-0.5 rounded-full bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400" />

                        {/* Body */}
                        <p className="text-sm sm:text-base md:text-lg leading-relaxed" style={{ color: "rgba(255,255,255,0.7)" }}>
                            In today's fast-paced digital landscape, effective IT management is essential for businesses to operate smoothly and efficiently. Our services are designed to optimize your technology infrastructure and support your business goals.
                        </p>

                        {/* 2x2 Cards — flip on desktop hover only, static on mobile */}
                        <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-2">
                            {cards.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <div key={item.title} className="flip-card h-24 sm:h-28">
                                        <div className="flip-card-inner">

                                            {/* Front */}
                                            <div
                                                className="flip-card-front p-3 sm:p-6 border flex items-center gap-2 sm:gap-3"
                                                style={{
                                                    background: "linear-gradient(135deg, rgba(147,51,234,0.08) 0%, rgba(37,99,235,0.08) 100%)",
                                                    borderColor: "rgba(147,51,234,0.2)",
                                                    boxShadow: "inset 0 0 20px rgba(37,99,235,0.06)",
                                                }}
                                            >
                                                <div
                                                    className="shrink-0 w-9 h-9 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-r from-purple-600 to-blue-600 flex items-center justify-center"
                                                    style={{ boxShadow: "0 4px 12px rgba(147,51,234,0.35)" }}
                                                >
                                                    <Icon className="w-4 h-4 sm:w-6 sm:h-6 text-white" />
                                                </div>
                                                <p className="text-white font-semibold text-xs sm:text-sm leading-snug">
                                                    {item.title}
                                                </p>
                                            </div>

                                            {/* Back */}
                                            <div
                                                className="flip-card-back p-3 sm:p-4 border flex items-center justify-center"
                                                style={{
                                                    background: "linear-gradient(135deg, rgba(147,51,234,0.15) 0%, rgba(37,99,235,0.15) 100%)",
                                                    borderColor: "rgba(147,51,234,0.3)",
                                                    boxShadow: "inset 0 0 24px rgba(147,51,234,0.08)",
                                                }}
                                            >
                                                <p className="text-xs sm:text-sm leading-relaxed text-center" style={{ color: "rgba(255,255,255,0.75)" }}>
                                                    {item.description}
                                                </p>
                                            </div>

                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
}